import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { AIParserIcon, Bin, DataPrepsIcon, InsightIcon, KnowledgeCenterOutlineIcon, LakeIcon, MonitoringIcon, MyProfileIcon, PipelineCreatorIcon, PipelinesIcon, SearchAltIcon, StatusesIcon, StorageIcon } from 'basic-components/icons';
import { CommonService } from 'dashboard-services/services';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { globalActions, loginActions } from 'primary-components';

import { statusesActions } from 'actions/modules/statuses';
import { pythonEditorActions, searchActions } from 'actions/modules/tools';
import properties from 'resources/constants/properties.json';
import { routing } from 'routing';

import Element from './Element';
import Header from './Header';
import Title from './Title';

import { When } from 'react-if';

import classNames from 'classnames';
import './SideBar.scss';

const SideBar = () => {
  const dispatch = useDispatch(),
        timeout = useRef(),
        hoverTimeout = useRef(),
        navigate = useNavigate(),

        { userName, storageScreen } = useSelector(state => state.myAccountState.settingsState),
        searchActive = useSelector(state => state.toolsState.searchState.searchAllOpen),
        statusesActive = useSelector(state => state.statusesState.open),
        fetchingRequests = useSelector(state => state.globalState.fetchingRequests),
        { pathname } = useLocation(),

        [animationStarted, setAnimationStarted] = useState(false),
        [expanded, setExpanded] = useState(false),
        [action, setAction] = useState(undefined),

        setExpandedFalse = useCallback(() => {
          setExpanded(false)
          setAction('click')
        }, []),
        changeExpanded = useCallback(() => {
          if(!expanded) {
            setAnimationStarted(true)
            if(statusesActive) {
              dispatch(statusesActions.changeValue(false, 'open'))
            }
            if(searchActive) {
              dispatch(searchActions.changeValue(false, 'searchAllOpen'))
            }
            clearTimeout(timeout.current)
            timeout.current = setTimeout(() => setAnimationStarted(false), 201)
          }
          setExpanded(e => !e)
          setAction('click')
        }, [expanded, statusesActive, searchActive, dispatch]),
        hasAccessTo = useCallback(component => dispatch(loginActions.hasAccessTo(component)), [dispatch]),
        hasMonitorAccess = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataScience.routing.dashboard.name ,"R", routing.dataScience.name)), [dispatch]),
        hasCreatorAccess = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataScience.routing.creator.name ,"R", routing.dataScience.name)), [dispatch]),
        hasPipelineListAccess = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataScience.routing.list.hashes.pipelines, "R", routing.dataScience.name)) || dispatch(loginActions.hasAccessTo(routing.dataScience.routing.task.name, "R", routing.dataScience.name)), [dispatch]),
        hasStorageListAcces = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataStorage.routing.list.name ,"R", routing.dataStorage.name)), [dispatch]),
        hasStorageCreatorAcces = useMemo(() => 
            dispatch(loginActions.hasAccessTo(routing.dataStorage.routing.creator.name ,"R", routing.dataStorage.name))
            ||
            dispatch(loginActions.hasAccessTo(routing.dataStorage.routing.oldCreator.name ,"R", routing.dataStorage.name))
        , [dispatch]),

        searchOnClick = useCallback(() => batch(() => {
          dispatch(searchActions.changeValue(!searchActive, 'searchAllOpen'))
          if(statusesActive) {
            dispatch(statusesActions.changeValue(false, 'open'))
          }
        }), [dispatch, searchActive, statusesActive]),
        statusesOnClick = useCallback(() => batch(() => {
          dispatch(statusesActions.changeValue(!statusesActive, 'open'))
          if(searchActive) {
            dispatch(searchActions.changeValue(false, 'searchAllOpen'))
          }
        }), [dispatch, statusesActive, searchActive]),
        startTimer = useCallback(() => {
          if(!expanded && !hoverTimeout.current) {
            hoverTimeout.current = setTimeout(() => {
              setExpanded(e => {
                if(!e) {
                  setAction('hover')
                  setAnimationStarted(true)
                  clearTimeout(timeout.current)
                  timeout.current = setTimeout(() => setAnimationStarted(false), 201)
                }
                return true
              })
            }, 1500)
          }
        }, [hoverTimeout, expanded]),
        clearTimer = useCallback(() => {
          clearTimeout(hoverTimeout.current)
          hoverTimeout.current = null;
          if(action === 'hover' && expanded) {
            setExpanded(false)
          }
        }, [hoverTimeout, expanded, action]),
        show = useCallback(path => {
          clearTimer()
          fetchingRequests.forEach(f => {
            f.abortController?.abort?.()
            dispatch(globalActions.handleRequest({ id: f.id, isResponse: true }));
          })
          batch(() => {
            dispatch(globalActions.clearRequests())
            dispatch(statusesActions.changeValue(false, 'open'))
            dispatch(searchActions.changeValue(false, 'searchAllOpen'))
            dispatch(pythonEditorActions.clear())
            navigate(path)
          })
          setExpandedFalse()
        }, [fetchingRequests, setExpandedFalse, clearTimer, dispatch, navigate]),
        learnMore = useCallback(() => CommonService.openInNewTab(DashboardLinkFactory.knowledgeCenter.root({ baseUrl: "" })), [])

  useEffect(() => () => {
    clearTimeout(hoverTimeout.current)
    clearTimeout(timeout.current)
  }, [])

  useEffect(() => {
    setExpandedFalse()
  }, [setExpandedFalse, searchActive, statusesActive])

  return (
    <Fragment>
      <div 
          className={classNames("sidebar", { "sidebar--is-expanded": expanded }, { "sidebar--discovery-only": !hasAccessTo(routing.dataLake.name) && !hasAccessTo(routing.dataStorage.name) && !hasAccessTo(routing.dataScience.name) })}
          onMouseEnter={startTimer}
          onMouseLeave={clearTimer}
      >
        <div className="sidebar__top">
          <Title
              animationStarted={animationStarted}
              expanded={expanded}
              setExpandedFalse={setExpandedFalse}
              switchExpanded={changeExpanded}
          />
          <When condition={hasAccessTo(routing.search.name) || hasAccessTo(routing.statuses.name)}>
            {() => (
              <div className="sidebar__group">
                <When condition={hasAccessTo(routing.search.name)}>
                  {() => (
                    <Element
                        animationStarted={animationStarted}
                        data-testid="search-all"
                        expanded={expanded}
                        icon={<SearchAltIcon/>}
                        onClick={searchOnClick}
                        selected={searchActive}
                        text={routing.search.readableName}
                    />
                  )}
                </When>
                <When condition={hasAccessTo(routing.statuses.name)}>
                  {() => (
                    <Element
                        animationStarted={animationStarted}
                        data-testid="statuses"
                        expanded={expanded}
                        icon={<StatusesIcon/>}
                        onClick={statusesOnClick}
                        selected={statusesActive}
                        text={routing.statuses.readableName}
                    />
                  )}
                </When>
              </div>
            )}
          </When>
          <When condition={hasAccessTo(routing.dataLake.name) || hasAccessTo(routing.dataStorage.name)}>
            {() => (
              <div className="sidebar__group">
                <Header
                    animationStarted={animationStarted}
                    expanded={expanded}
                    onDotClick={changeExpanded}
                    text="DATA"
                />
                <When condition={hasAccessTo(routing.dataLake.name)}>
                  {() => (
                    <Element
                        animationStarted={animationStarted}
                        data-testid="lake"
                        expanded={expanded}
                        icon={<LakeIcon/>}
                        onClick={show}
                        path={DashboardLinkFactory.dataLake.list({ baseUrl: "", query: "", tab: routing.dataLake.routing.groups.name })}
                        selected={String(pathname).startsWith(`/${routing.dataLake.name}`)}
                        text={routing.dataLake.readableName}
                    />
                  )}
                </When>
                <When condition={hasAccessTo(routing.dataStorage.name) && hasStorageListAcces}>
                  {() => (
                    <Element
                        animationStarted={animationStarted}
                        data-testid="datapreps"
                        expanded={expanded}
                        icon={<DataPrepsIcon/>}
                        onClick={show}
                        path={DashboardLinkFactory.dataStorage.list({ baseUrl: "", query: "" })}
                        selected={pathname === `/${routing.dataStorage.name}`}
                        text="DATA PREPS"
                    />
                  )}
                </When>
                <When condition={hasAccessTo(routing.dataStorage.name) && hasStorageCreatorAcces}>
                  {() => (
                    <Element
                        animationStarted={animationStarted}
                        data-testid="storage"
                        expanded={expanded}
                        icon={<StorageIcon/>}
                        onClick={show}
                        path={DashboardLinkFactory.dataStorage.newDataPrep({ baseUrl: "", storageScreen })}
                        selected={String(pathname).startsWith(`/${routing.dataStorage.name}/${routing.dataStorage.routing.creator.name}`)}
                        text={routing.dataStorage.readableName}
                    />
                  )}
                </When>
              </div>
            )}
          </When>
          <When condition={(hasAccessTo(routing.dataScience.name) && hasPipelineListAccess) || (hasAccessTo(routing.dataScience.name)  && hasMonitorAccess) || (hasAccessTo(routing.dataScience.name) && hasCreatorAccess)}>
            {() => (
              <div className="sidebar__group">
                <Header
                    animationStarted={animationStarted}
                    expanded={expanded}
                    onDotClick={changeExpanded}
                    text={routing.dataScience.readableName}
                />
                <When condition={hasAccessTo(routing.dataScience.name) && hasPipelineListAccess}>
                  {() => (
                    <Element
                        animationStarted={animationStarted}
                        data-testid="list"
                        expanded={expanded}
                        icon={<PipelinesIcon/>}
                        onClick={show}
                        path={DashboardLinkFactory.dataScience.list({ baseUrl: "", query: "", tab: routing.dataScience.routing.list.hashes.pipelines })}
                        selected={pathname === `/${routing.dataScience.name}` || String(pathname).includes(`/${routing.dataScience.name}/Versions`) || String(pathname).includes(`/${routing.dataScience.name}/Task/Versions`) || String(pathname).includes(`/${routing.dataScience.name}/Task/Creator`)}
                        text={routing.dataScience.routing.list.name}
                    />
                  )}
                </When>
                <When condition={hasAccessTo(routing.dataScience.name) && hasMonitorAccess}>
                  {() => (
                    <Element
                        animationStarted={animationStarted}
                        data-testid="monitoring"
                        expanded={expanded}
                        icon={<MonitoringIcon/>}
                        onClick={show}
                        path={DashboardLinkFactory.dataScience.monitor({ baseUrl: "", tab: "Recent" })}
                        selected={String(pathname).startsWith(`/${routing.dataScience.name}/${routing.dataScience.routing.dashboard.name}`)}
                        text="MONITORING"
                    />
                  )}
                </When>
                <When condition={hasAccessTo(routing.dataScience.name) && hasCreatorAccess}>
                  {() => (
                    <Element
                        animationStarted={animationStarted}
                        data-testid="creator"
                        expanded={expanded}
                        icon={<PipelineCreatorIcon/>}
                        onClick={show}
                        path={DashboardLinkFactory.dataScience.newPipeline({ baseUrl: "" })}
                        selected={String(pathname).startsWith(`/${routing.dataScience.name}/${routing.dataScience.routing.creator.name}`)}
                        text={routing.dataScience.routing.creator.name}
                    />
                  )}
                </When>
              </div>
            )}
          </When>
        </div>
        <div className={classNames("sidebar__bottom", { "sidebar__bottom--discovery-only": !hasAccessTo(routing.dataLake.name) && !hasAccessTo(routing.dataStorage.name) && !hasAccessTo(routing.dataScience.name) && !hasAccessTo(routing.aiTools.name) })}>
          <div className="sidebar__group">
            <Header
                animationStarted={animationStarted}
                expanded={expanded}
                onDotClick={changeExpanded}
                text="DISCOVER"
            />
            <When condition={hasAccessTo(routing.aiTools.name)}>
              {() => (
                <Element
                    animationStarted={animationStarted}
                    data-testid="aiTools"
                    expanded={expanded}
                    icon={<AIParserIcon/>}
                    onClick={show}
                    path={DashboardLinkFactory.aiTools.root({ baseUrl: "" })}
                    selected={String(pathname).startsWith(`/${routing.aiTools.name}`)}
                    text={routing.aiTools.readableName}
                />
              )}
            </When>
            <Element
                animationStarted={animationStarted}
                data-testid="knowledge-center"
                expanded={expanded}
                icon={<KnowledgeCenterOutlineIcon/>}
                onClick={learnMore}
                selected={false}
                text="KNOWLEDGE"
            />
            <When condition={hasAccessTo(routing.insight.name)}>
              {() => (
                <Element
                    animationStarted={animationStarted}
                    data-testid="insight"
                    expanded={expanded}
                    icon={<InsightIcon/>}
                    onClick={show}
                    path={DashboardLinkFactory.insight.list({ baseUrl: "" })}
                    selected={String(pathname).startsWith(`/${routing.insight.name}`)}
                    text={routing.insight.readableName}
                />
              )}
            </When>
          </div>
          <div className="sidebar__group">
            <Header
                animationStarted={animationStarted}
                expanded={expanded}
                onDotClick={changeExpanded}
                text="SETTINGS"
            />
            <When condition={properties.warehouse_only === "true" || properties.warehouse_hybrid === "true"}>
              {() => (
                <Element
                    animationStarted={animationStarted}
                    data-testid="trash"
                    expanded={expanded}
                    icon={
                      <Bin strokeWidth={1}/>
                    }
                    onClick={show}
                    path={DashboardLinkFactory.trash.list({ baseUrl: "" })}
                    selected={String(pathname).startsWith(`/${routing.trash.name}`)}
                    text="TRASH"
                    tooltipText={expanded ? undefined : userName}
                />
              )}
            </When>
            <Element
                animationStarted={animationStarted}
                data-testid="my-account"
                expanded={expanded}
                icon={<MyProfileIcon/>}
                onClick={show}
                path={DashboardLinkFactory.myAccount.list({ baseUrl: "" })}
                selected={String(pathname).startsWith(`/${routing.myAccount.name}`)}
                text="PROFILE"
                tooltipText={expanded ? undefined : userName}
            />
          </div>
        </div>
      </div>
      <When condition={expanded && !animationStarted}>
        {() => (
          <div
              className="sidebar-background"
              onClick={setExpandedFalse}
          />
        )}
      </When>
    </Fragment>
  )
}

export default memo(SideBar)
